/**
 * Returns the URL for an asset (logo or banner).
 * If the user has uploaded a blob to IndexedDB, returns an object URL; otherwise returns the API fallback URL.
 * Listens for 'catalogue-asset-changed' so the URL updates after upload.
 */

import { useState, useEffect } from 'react';
import { db } from '../shared/services/db';

function loadAssetUrl(assetId: string, fallbackUrl: string | null): Promise<string | null> {
  return db.assetBlobs.get(assetId).then(row => {
    if (row?.blob) return URL.createObjectURL(row.blob);
    return fallbackUrl;
  });
}

export function useAssetUrl(
  assetId: string,
  fallbackUrl: string | null
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    loadAssetUrl(assetId, fallbackUrl).then(u => {
      objectUrl = u && u.startsWith('blob:') ? u : null;
      setUrl(u);
    });
    const onChanged = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      loadAssetUrl(assetId, fallbackUrl).then(u => {
        objectUrl = u && u.startsWith('blob:') ? u : null;
        setUrl(u);
      });
    };
    window.addEventListener('catalogue-asset-changed', onChanged);
    return () => {
      window.removeEventListener('catalogue-asset-changed', onChanged);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, fallbackUrl]);

  return url;
}
